import type {
  RuteroConnectorOccupancyLimits,
  RuteroOfferOptionsSelectionPreview,
  RuteroOccupancy,
  RuteroOfferQuoteCategory,
  RuteroSelectedSupplement,
  RuteroOfferQuoteSupplement,
} from '@bandido/shared/types';
import { RUTERO_HOLD_PASSENGER_MAX } from '@bandido/shared/types';

import type { ConnectorInfo, RuteroPassengerPayload } from './queries';

const MAX_CONTRACT_ROOMS = 9;
const MAX_CONTRACT_PAX_PER_ROOM = 9;

export interface CircuitQuoteCatalogue {
  categories: RuteroOfferQuoteCategory[];
  supplements: RuteroOfferQuoteSupplement[];
}

export function emptyCircuitQuoteCatalogue(): CircuitQuoteCatalogue {
  return { categories: [], supplements: [] };
}

/**
 * Solo una respuesta cotizada, fresca y correspondiente a la selección viva
 * puede reemplazar el catálogo editable de Circuitos. Los estados rejected y
 * unavailable no traen catálogo: conservar el último evita borrar elecciones
 * y disparar una nueva cotización para un pedido distinto al rechazado.
 */
export function authoritativeCircuitQuoteCatalogue({
  preview,
  selectionMatches,
  isFetching,
  isPreviousData,
}: {
  preview: RuteroOfferOptionsSelectionPreview | undefined;
  selectionMatches: boolean;
  isFetching: boolean;
  isPreviousData: boolean;
}): CircuitQuoteCatalogue | undefined {
  if (
    preview?.state !== 'priced' ||
    !selectionMatches ||
    isFetching ||
    isPreviousData
  ) {
    return undefined;
  }
  return {
    categories: preview.categories ?? [],
    supplements: preview.supplements ?? [],
  };
}

export function circuitOccupancyFromParam(
  value: string | undefined,
): RuteroOccupancy | undefined {
  if (!value) return undefined;
  const rooms = value.split('|').map((room) => {
    const ageTokens = room.split(',');
    return {
      ages: ageTokens.every((age) => /^\d{1,3}$/.test(age))
        ? ageTokens.map(Number)
        : [Number.NaN],
    };
  });
  if (
    rooms.length < 1 ||
    rooms.length > MAX_CONTRACT_ROOMS ||
    rooms.some(
      ({ ages }) =>
        ages.length < 1 ||
        ages.length > MAX_CONTRACT_PAX_PER_ROOM ||
        ages.some((age) => !Number.isInteger(age) || age < 0 || age > 120),
    )
  ) {
    return undefined;
  }
  return { rooms };
}

export function circuitOccupancyParam(
  occupancy: RuteroOccupancy | undefined,
): string | undefined {
  return occupancy?.rooms.length
    ? occupancy.rooms.map((room) => room.ages.join(',')).join('|')
    : undefined;
}

export function circuitOccupancyCount(
  occupancy: RuteroOccupancy | undefined,
): number {
  return (
    occupancy?.rooms.reduce((total, room) => total + room.ages.length, 0) ?? 0
  );
}

export function defaultCircuitOccupancy(
  limits: RuteroConnectorOccupancyLimits,
  adults = 2,
): RuteroOccupancy {
  const maximum = Math.max(
    1,
    Math.min(
      limits.maxPaxPerRoom ?? MAX_CONTRACT_PAX_PER_ROOM,
      limits.maxPax ?? RUTERO_HOLD_PASSENGER_MAX,
      RUTERO_HOLD_PASSENGER_MAX,
    ),
  );
  return {
    rooms: [
      {
        ages: Array.from(
          { length: Math.max(1, Math.min(adults, maximum)) },
          () => limits.defaultAdultAge,
        ),
      },
    ],
  };
}

export function circuitOccupancyValid(
  occupancy: RuteroOccupancy | undefined,
  limits: RuteroConnectorOccupancyLimits | undefined,
): boolean {
  if (!occupancy || !limits) return false;
  const total = circuitOccupancyCount(occupancy);
  return (
    occupancy.rooms.length >= 1 &&
    occupancy.rooms.length <= limits.maxRooms &&
    occupancy.rooms.every(
      (room) =>
        room.ages.length >= 1 &&
        room.ages.length <=
          (limits.maxPaxPerRoom ?? MAX_CONTRACT_PAX_PER_ROOM) &&
        room.ages.every(
          (age) => Number.isInteger(age) && age >= 0 && age <= 120,
        ),
    ) &&
    total <=
      Math.min(
        limits.maxPax ?? RUTERO_HOLD_PASSENGER_MAX,
        RUTERO_HOLD_PASSENGER_MAX,
      )
  );
}

export function circuitOccupancyLimitsFor(
  connectors: readonly ConnectorInfo[] | undefined,
  provider: string | undefined,
): RuteroConnectorOccupancyLimits | undefined {
  if (!provider) return undefined;
  const matches =
    connectors?.filter(
      (connector) =>
        connector.vertical === 'circuits' && connector.provider === provider,
    ) ?? [];
  return matches.length === 1 ? matches[0].occupancyLimits : undefined;
}

export function setCircuitRoomCategory(
  occupancy: RuteroOccupancy,
  roomIndex: number,
  categoryId: string | undefined,
  sameCategoryForAllRooms: boolean,
): RuteroOccupancy {
  return {
    rooms: occupancy.rooms.map((room, index) => ({
      ages: [...room.ages],
      ...((sameCategoryForAllRooms || index === roomIndex) && categoryId
        ? { categoryId }
        : !sameCategoryForAllRooms && index !== roomIndex && room.categoryId
          ? { categoryId: room.categoryId }
          : {}),
    })),
  };
}

function supplementIdentity(selection: RuteroSelectedSupplement): string {
  return [
    selection.code,
    selection.roomIndex ?? '',
    selection.passengerIndex ?? '',
  ].join(':');
}

export function setCircuitSupplement(
  current: readonly RuteroSelectedSupplement[],
  next: RuteroSelectedSupplement | undefined,
  identity: Pick<
    RuteroSelectedSupplement,
    'code' | 'roomIndex' | 'passengerIndex'
  >,
): RuteroSelectedSupplement[] {
  const key = supplementIdentity(identity);
  const without = current.filter(
    (selection) => supplementIdentity(selection) !== key,
  );
  return next ? [...without, next] : without;
}

export function circuitSupplementsValid(
  selected: readonly RuteroSelectedSupplement[],
  published: readonly RuteroOfferQuoteSupplement[] | undefined,
  occupancy?: RuteroOccupancy,
): boolean {
  return selected.every((selection) => {
    const supplement = published?.find(
      (entry) => entry.code === selection.code,
    );
    if (!supplement) return false;
    if (
      occupancy &&
      !circuitSupplementSelectionEligible(selection, supplement, occupancy)
    ) {
      return false;
    }
    if (
      supplement.per === 'night' &&
      (selection.quantity === undefined ||
        selection.quantity < 1 ||
        (supplement.limits?.maxNights !== undefined &&
          selection.quantity > supplement.limits.maxNights))
    ) {
      return false;
    }
    if (supplement.requiresDetail !== 'transfer') return true;
    return Boolean(
      selection.transfer?.airport.trim() &&
        selection.transfer.flightNumber.trim() &&
        selection.transfer.dateTime.trim(),
    );
  });
}

export function circuitSupplementEligible(
  supplement: RuteroOfferQuoteSupplement,
  occupancy: RuteroOccupancy,
  roomIndex: number,
  passengerIndex?: number,
): boolean {
  const room = occupancy.rooms[roomIndex];
  if (!room) return false;
  if (supplement.categoryId && room.categoryId !== supplement.categoryId) return false;
  if (passengerIndex === undefined) return true;
  const age = occupancy.rooms.flatMap((entry) => entry.ages)[passengerIndex];
  if (age === undefined) return false;
  return (
    (supplement.ageFrom === undefined || age >= supplement.ageFrom) &&
    (supplement.ageTo === undefined || age <= supplement.ageTo)
  );
}

function circuitSupplementSelectionEligible(
  selection: RuteroSelectedSupplement,
  supplement: RuteroOfferQuoteSupplement,
  occupancy: RuteroOccupancy,
): boolean {
  if (supplement.per === 'booking' || supplement.per === 'night') {
    return (
      selection.roomIndex === undefined &&
      selection.passengerIndex === undefined
    );
  }
  if (supplement.per === 'room') {
    return (
      selection.roomIndex !== undefined &&
      selection.passengerIndex === undefined &&
      circuitSupplementEligible(supplement, occupancy, selection.roomIndex)
    );
  }
  if (selection.passengerIndex === undefined) return false;
  const roomIndex = occupancy.rooms.findIndex((_, candidateRoomIndex) => {
    const before = occupancy.rooms
      .slice(0, candidateRoomIndex)
      .reduce((total, room) => total + room.ages.length, 0);
    return selection.passengerIndex! >= before &&
      selection.passengerIndex! < before + occupancy.rooms[candidateRoomIndex].ages.length;
  });
  return (
    roomIndex >= 0 &&
    selection.roomIndex === undefined &&
    circuitSupplementEligible(
      supplement,
      occupancy,
      roomIndex,
      selection.passengerIndex,
    )
  );
}

export function reconcileCircuitSupplements(
  selected: RuteroSelectedSupplement[],
  published: readonly RuteroOfferQuoteSupplement[] | undefined,
  occupancy: RuteroOccupancy,
): RuteroSelectedSupplement[] {
  const reconciled = selected.filter((selection) => {
    const supplement = published?.find((entry) => entry.code === selection.code);
    return Boolean(
      supplement &&
      circuitSupplementSelectionEligible(selection, supplement, occupancy),
    );
  });
  // React usa esta referencia para construir la selección que dispara el
  // quote. Si el catálogo no eliminó nada, conservarla hace converger el
  // efecto de reconciliación y evita recotizar indefinidamente.
  return reconciled.length === selected.length ? selected : reconciled;
}

export function passengersWithCircuitRooms(
  passengers: readonly RuteroPassengerPayload[],
  occupancy: RuteroOccupancy | undefined,
): RuteroPassengerPayload[] {
  const roomIndexes = occupancy?.rooms.flatMap(
    (room, roomIndex) => room.ages.map(() => roomIndex),
  ) ?? [];
  return passengers.map((passenger, index) => ({
    ...passenger,
    ...(roomIndexes[index] === undefined
      ? {}
      : { roomIndex: roomIndexes[index] }),
  }));
}
